import { t } from '@lingui/macro'
import { createStyles, makeStyles, Theme, useMediaQuery, useTheme } from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import React, { Fragment, FunctionComponent, useEffect } from 'react'
import { useJitsi } from 'react-jutsu'
import AppointmentEventLogData from '../Models/AppointmentEventLogData'
import JitsiOptions from '../Utils/JitsiOptions'
import ConfirmationMessage from './ConfirmationMessage'

export interface ChatRoomProps {
  eventLog: AppointmentEventLogData
  chatRoom: string
}

const VIDEO_QUALITY_HD = 720

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    logoImgNormal: {
      width: '140px',
      position: 'fixed',
      top: 100,
      left: 32,
      zIndex: 10000,
      opacity: 0.5
    },
    logoImgSmall: {
      width: '20%',
      position: 'fixed',
      top: 100,
      left: 32,
      zIndex: 10000,
      opacity: 0.5
    }
  })
})

const ChatRoom: FunctionComponent<ChatRoomProps> = ({ eventLog, chatRoom }) => {
  const theme = useTheme()
  const classes = useStyles(theme)
  const [closable, setClosable] = React.useState(true)
  const [openConfirmationMessage, setOpenConfirmationMessage] = React.useState(false)
  const titleConfirmationMessage = t`Ayúdanos a mejorar`
  const contentConfirmationMessage = t`Ayúdanos a mejorar nuestro servicio a través de 2 preguntas. Tiempo: 3 minutos.\n ¿Deseas Realizar la encuesta?`

  const smallScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const satisfactionSurveyUrl = (window as any).ChatroomConfig?.satisfactionSurveyUrl ?? ''
  const DomainJitsi = (window as any).ChatroomConfig?.domainJitsi ?? ''

  const openSurvey = () => {
    window.location.replace(satisfactionSurveyUrl)
  }

  useEffect(() => {
    if (closable) {
      window.onbeforeunload = () => {
        handleOpenConfirmationMessage()
        return contentConfirmationMessage
      }
    } else {
      window.onbeforeunload = null
    }

    return () => {
      window.onbeforeunload = null
    }
  }, [closable, contentConfirmationMessage])

  const handleOpenConfirmationMessage = () => {
    setClosable(false)
    setOpenConfirmationMessage(true)
  }

  // Preservamos cualquier configOverwrite existente en JitsiOptions.
  const baseConfigOverwrite = (JitsiOptions as any).configOverwrite ?? {}
  const baseConstraints = baseConfigOverwrite.constraints ?? {}
  const baseVideoConstraints = baseConstraints.video ?? {}
  const baseHeightConstraints = baseVideoConstraints.height ?? {}
  const baseVideoQuality = baseConfigOverwrite.videoQuality ?? {}
  const baseMinHeightForQualityLvl = baseVideoQuality.minHeightForQualityLvl ?? {}

  const jitsiConfig = {
    ...JitsiOptions,

    // La IFrame API permite sobrescribir config.js mediante configOverwrite.
    configOverwrite: {
      ...baseConfigOverwrite,

      // Resolución preferida de captura local.
      resolution: VIDEO_QUALITY_HD,

      // En teleconsulta 1:1 no queremos que la cantidad de participantes
      // obligue a reducir HD a SD en tile view.
      maxFullResolutionParticipants: -1,

      // Constraints de captura independientes de "resolution".
      constraints: {
        ...baseConstraints,
        video: {
          ...baseVideoConstraints,
          height: {
            ...baseHeightConstraints,
            ideal: VIDEO_QUALITY_HD,
            max: VIDEO_QUALITY_HD,
            min: 240
          }
        }
      },

      // Para una interfaz 1:1 queremos solicitar HD desde tiles de 360 px
      // de alto. Jitsi conserva su adaptación dinámica si la red no soporta HD.
      videoQuality: {
        ...baseVideoQuality,
        minHeightForQualityLvl: {
          ...baseMinHeightForQualityLvl,
          180: 'low',
          240: 'standard',
          360: 'high'
        }
      }
    },

    userInfo: {
      displayName: eventLog.RolinCall === 'Patient' ? eventLog.PatientName : eventLog.SpecialistName
    },
    subject: eventLog.ExamName,
    parentNode: 'jitsi-container',
    roomName: chatRoom,
    onMeetingEnd: handleOpenConfirmationMessage,
    domain: DomainJitsi
  }

  const { error, jitsi } = useJitsi(jitsiConfig)

  useEffect(() => {
    if (!jitsi) {
      return
    }

    const handleVideoConferenceJoined = () => {
      // setVideoQuality define la resolución máxima de envío y recepción.
      jitsi.executeCommand('setVideoQuality', VIDEO_QUALITY_HD)
    }

    // La API actual de JitsiMeetExternalAPI implementa EventEmitter.
    jitsi.addListener('videoConferenceJoined', handleVideoConferenceJoined)

    return () => {
      jitsi.removeListener('videoConferenceJoined', handleVideoConferenceJoined)
      jitsi.dispose()
    }
  }, [jitsi])

  return (
    <Fragment>
      <img
        className={smallScreen ? classes.logoImgSmall : classes.logoImgNormal}
        src='https://imedicalmarketing.blob.core.windows.net/portal-imedical/logos/logo-transparente.png?sv=2025-07-05&spr=https&st=2026-05-28T16%3A22%3A15Z&se=2027-01-01T16%3A22%3A00Z&sr=b&sp=r&sig=tC8SbpCNaAXGLsV2g%2BOeEuZ29XaTnGbEW4aZXJ0xZnw%3D'
        alt='iMedicalCloud'
      />
      {error && <Alert severity='error'>{error}</Alert>}
      <ConfirmationMessage
        open={openConfirmationMessage}
        setOpen={setOpenConfirmationMessage}
        onClickAccept={openSurvey}
        title={titleConfirmationMessage}
        content={contentConfirmationMessage}
      />
      <div id={jitsiConfig.parentNode} style={{ height: '100%', width: '100%', position: 'absolute' }} />
    </Fragment>
  )
}

export default ChatRoom
